import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getRecognitionData, saveRecognition, GetRecognitionDataOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Star, Trophy, Plus, ThumbsUp, Zap, CheckCircle2, XCircle } from 'lucide-react';

export default function Recognition() {
  const { user } = useAuth();
  const [data, setData] = useState<GetRecognitionDataOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [nomDialog, setNomDialog] = useState(false);
  const [form, setForm] = useState({ title: '', nomineeId: '', category: '', reason: '', pointsAwarded: 10, visibleToTeam: true });

  useEffect(() => { if (user) load(); }, [user]);
  const load = () => { setLoading(true); getRecognitionData({}).then(setData).finally(() => setLoading(false)); };

  const handleNominate = async () => {
    if (!form.title || !form.nomineeId) return;
    try {
      await saveRecognition({ action: 'nominate', ...form });
      toast.success('Recognition submitted');
      setNomDialog(false);
      setForm({ title: '', nomineeId: '', category: '', reason: '', pointsAwarded: 10, visibleToTeam: true });
      load();
    } catch { toast.error('Failed'); }
  };

  const handleApproval = async (id: string, action: 'approve' | 'decline') => {
    try {
      await saveRecognition({ action, id });
      toast.success(action === 'approve' ? 'Approved' : 'Declined');
      load();
    } catch { toast.error('Failed'); }
  };

  const catColors: Record<string, string> = { 'Customer Service': 'bg-blue-500/10 text-blue-700', 'Teamwork': 'bg-green-500/10 text-green-700', 'Initiative': 'bg-purple-500/10 text-purple-700', 'Training': 'bg-orange-500/10 text-orange-700', 'Going Above & Beyond': 'bg-yellow-500/10 text-yellow-700', Other: 'bg-muted text-muted-foreground' };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>;

  const feed = (data?.recognitions || []).filter((r: any) => r.status === 'Approved' && r.visibleToTeam);
  const pendingApproval = (data?.recognitions || []).filter((r: any) => r.status === 'Pending');
  const myRewards = data?.myRecognitions || [];

  const getName = (id: string) => { const s = data?.staffList?.find((u: any) => u.id === id); return s ? `${s.firstName} ${s.lastName}` : 'Staff'; };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Trophy className="w-6 h-6 text-primary" /> Rewards & Recognition</h1>
          <p className="text-sm text-muted-foreground mt-1">Celebrate achievements and earn points</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-primary font-semibold"><Zap className="w-4 h-4" />{data?.pointsBalance || 0} pts</div>
          {data?.isManager && <Button size="sm" onClick={() => setNomDialog(true)}><Plus className="w-4 h-4 mr-1" /> Nominate</Button>}
        </div>
      </div>

      <Tabs defaultValue="feed">
        <TabsList>
          <TabsTrigger value="feed">Team Feed ({feed.length})</TabsTrigger>
          <TabsTrigger value="my">My Recognitions ({myRewards.length})</TabsTrigger>
          {data?.isAdmin && <TabsTrigger value="pending">Pending ({pendingApproval.length})</TabsTrigger>}
          <TabsTrigger value="rewards">Rewards Shop</TabsTrigger>
        </TabsList>

        <TabsContent value="feed" className="space-y-3 mt-4">
          {feed.length === 0 ? <p className="text-muted-foreground text-sm text-center py-8">No recognitions yet</p> : feed.map((r: any) => {
            const nomineeId = Array.isArray(r.nominee) ? r.nominee[0] : r.nominee;
            return (
              <Card key={r.id}>
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Star className="w-5 h-5 text-primary" /></div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{r.title}</h3>
                      <p className="text-sm text-muted-foreground">🎉 {getName(nomineeId)}</p>
                      <p className="text-sm mt-1">{r.reason}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={catColors[r.category] || 'bg-muted text-muted-foreground'}>{r.category}</Badge>
                        {(r.pointsAwarded || 0) > 0 && <Badge variant="outline" className="text-xs"><Zap className="w-3 h-3 mr-1" />{r.pointsAwarded} pts</Badge>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="my" className="space-y-3 mt-4">
          {myRewards.length === 0 ? <p className="text-muted-foreground text-sm text-center py-8">No recognitions yet</p> : myRewards.map((r: any) => (
            <Card key={r.id}>
              <CardContent className="py-4 flex justify-between items-center">
                <div>
                  <h3 className="font-semibold">{r.title}</h3>
                  <p className="text-sm text-muted-foreground">{r.reason}</p>
                  <Badge className={catColors[r.category] || ''} >{r.category}</Badge>
                </div>
                <div className="text-right">
                  <Badge variant={r.status === 'Approved' ? 'default' : 'outline'}>{r.status}</Badge>
                  {(r.pointsAwarded || 0) > 0 && <p className="text-xs text-primary mt-1"><Zap className="w-3 h-3 inline mr-0.5" />{r.pointsAwarded}</p>}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {data?.isAdmin && (
          <TabsContent value="pending" className="space-y-3 mt-4">
            {pendingApproval.map((r: any) => {
              const nomineeId = Array.isArray(r.nominee) ? r.nominee[0] : r.nominee;
              return (
                <Card key={r.id}>
                  <CardContent className="py-4 flex justify-between items-center gap-3">
                    <div>
                      <h3 className="font-semibold">{r.title}</h3>
                      <p className="text-sm text-muted-foreground">For: {getName(nomineeId)} · {r.reason}</p>
                      <Badge className={catColors[r.category] || ''}>{r.category}</Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleApproval(r.id, 'approve')} className="text-green-600"><CheckCircle2 className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => handleApproval(r.id, 'decline')} className="text-red-600"><XCircle className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        )}

        <TabsContent value="rewards" className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(data?.rewardItems || []).map((item: any) => (
              <Card key={item.id}>
                <CardContent className="py-4 text-center">
                  <h3 className="font-semibold">{item.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                  <p className="text-primary font-bold mt-2"><Zap className="w-4 h-4 inline mr-1" />{item.pointsCost} pts</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={nomDialog} onOpenChange={setNomDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nominate for Recognition</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title (e.g. Amazing Customer Service)" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            <Select value={form.nomineeId} onValueChange={v => setForm({ ...form, nomineeId: v })}>
              <SelectTrigger><SelectValue placeholder="Select staff member" /></SelectTrigger>
              <SelectContent>{(data?.staffList || []).map((s: any) => <SelectItem key={s.id} value={s.id}>{s.firstName} {s.lastName}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>{['Customer Service','Teamwork','Initiative','Training','Going Above & Beyond','Other'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea placeholder="Reason" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} />
            <Input type="number" placeholder="Points to award" value={form.pointsAwarded} onChange={e => setForm({ ...form, pointsAwarded: parseInt(e.target.value) || 0 })} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setNomDialog(false)}>Cancel</Button><Button onClick={handleNominate}>Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
